/**
 * MIRRORED FILE — client/src/utils/quotationConfirmationRules.ts <-> src/utils/quotationConfirmationRules.js
 * Change one, change both. `src/__tests__/mirroredUtilParity.test.js` is the
 * list and the enforcement; a twin that is not in it is an orphan the moment it
 * is written.
 *
 * ---------------------------------------------------------------------------
 * WHAT STOPS A QUOTATION BECOMING A CONFIRMED ORDER
 * ---------------------------------------------------------------------------
 *
 * ClickUp 86d478wf8. `handleConfirmOrder` called `manualConfirmOrder` with NO
 * preconditions checked on either side: a quotation with an unpriced line, or a
 * line selling below cost, became a confirmed order and then a vendor PO and a
 * client invoice, with nothing between it and the money.
 *
 * **Owner decision 2026-09-02 — exactly three rules block a confirmation, and
 * the block is hard.** Three further candidates were offered and declined:
 * currency resolved and pinned, client and vessel set, and unresolved pack
 * size / lines flagged for review. They are named here so the next person knows
 * they were considered rather than forgotten.
 *
 * **A fourth rule, owner decision D51 (2026-09-22, QUO-00054):** a line quoted
 * in a unit other than the one the vessel asked in must state the ask restated
 * through its own pack (`quotationLineAmount`) — 20 CASE of water went out as
 * 20 L. Hard like the other three, cleared by correcting the amount, a recorded
 * part-pack round-up, or a person saying the amount is right on the line.
 *
 * **This file is the convenience mirror, NEVER the gate.** The gate is
 * `confirmedOrder.service` — `confirmQuotationOrder` for the manual flow and
 * `createConfirmedOrderFromQuotation` for every other caller — which refuses
 * with an AppError and a code. What this buys is the screen listing what is blocking
 * BEFORE the click instead of failing after it.
 *
 * Registered in docs/BUSINESS_RULES.md.
 */

// `resolveItemPricing` is the one owner of "what is this line quoted at", and
// the server twin answers identically — the parity harness proves it.
import { resolveItemPricing, costAbovePrice, DEFAULT_MARKUP_PERCENT } from './pricing';
// D51: does the line still state what the vessel asked for, in its own unit.
import {
  LINE_AMOUNT_BLOCKER_CODE,
  lineAmountCheck,
  blocksQuotation,
  describeLineAmount,
} from './quotationLineAmount';

export interface ConfirmationBlocker {
  code: string;
  lineNumber: number;
  itemName: string;
  message: string;
}

/**
 * Machine-readable reasons a confirmation is refused. The UI switches on these;
 * it never reads the prose beside them (invariant 6).
 */
export const CONFIRMATION_BLOCKER_CODES = Object.freeze({
  NOT_PRICED: 'QUOTATION_LINE_NOT_PRICED',
  NEGATIVE_SELLING_PRICE: 'QUOTATION_LINE_NEGATIVE_SELLING_PRICE',
  NEGATIVE_MARKUP: 'QUOTATION_LINE_NEGATIVE_MARKUP',
  // D51 — the amount on the line is not the vessel's ask in the line's unit.
  AMOUNT_NOT_THE_ASK: LINE_AMOUNT_BLOCKER_CODE,
});

/** The code the whole refusal carries, whatever the individual line problems are. */
export const QUOTATION_NOT_CONFIRMABLE = 'QUOTATION_NOT_CONFIRMABLE';

function lineLabel(item: any, index: number): string {
  const n = item && item.lineNumber != null ? item.lineNumber : index + 1;
  const name = item && item.itemName ? ` (${item.itemName})` : '';
  return `Line ${n}${name}`;
}

/**
 * Everything blocking this quotation from being confirmed, one entry per
 * problem, in line order. Empty means it may be confirmed.
 */
export function confirmationBlockers(
  quotation: any,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): ConfirmationBlocker[] {
  const items = quotation && Array.isArray(quotation.items) ? quotation.items : [];
  const blockers: ConfirmationBlocker[] = [];

  items.forEach((item: any, index: number) => {
    const lineNumber = item && item.lineNumber != null ? item.lineNumber : index + 1;
    const itemName = (item && item.itemName) || '';
    const add = (code: string, message: string) =>
      blockers.push({ code, lineNumber, itemName, message });

    // RULE 4 (D51) — asked FIRST and without returning, because it is about
    // the AMOUNT, not the money: a line can be priced and still quote 20 L for
    // 20 CASE, and the rules below would pass it.
    const amount = lineAmountCheck(item);
    if (blocksQuotation(amount)) {
      add(CONFIRMATION_BLOCKER_CODES.AMOUNT_NOT_THE_ASK, `${lineLabel(item, index)}: ${describeLineAmount(amount)}`);
    }

    const pricing = resolveItemPricing(item, defaultMarkupPercent);

    // RULE 2 BEFORE RULE 1, deliberately. `resolveItemPricing` only accepts a
    // STORED selling price when it is `> 0`, so a stored -5 falls through to the
    // markup-derived figure and the resolved value is positive — the negative is
    // invisible by the time rule 1 looks. The stored field is therefore read
    // raw here, and it is the only place in this module that does.
    const storedUnitSelling = Number(item && item.unitSellingPrice);
    const storedTotalSelling = Number(item && item.totalSellingPrice);
    const negativeStored =
      (Number.isFinite(storedUnitSelling) && storedUnitSelling < 0) ||
      (Number.isFinite(storedTotalSelling) && storedTotalSelling < 0);

    if (negativeStored) {
      add(
        CONFIRMATION_BLOCKER_CODES.NEGATIVE_SELLING_PRICE,
        `${lineLabel(item, index)} has a negative selling price.`
      );
      // One problem per line is enough to stop it; piling the derived
      // consequences on top would make the list unreadable for one bad number.
      return;
    }

    // RULE 1 — every line priced. Asked of the RESOLVED price, not the stored
    // one: a line with no stored selling price but a real cost IS priced, at the
    // quotation's markup. What this catches is a line that resolves to nothing.
    if (!(pricing.unitSellingPrice > 0) || !(pricing.totalSellingPrice > 0)) {
      add(
        CONFIRMATION_BLOCKER_CODES.NOT_PRICED,
        `${lineLabel(item, index)} has no selling price.`
      );
      return;
    }

    // RULE 3 — no negative realised markup: the line may not sell below cost.
    //
    // NOTE: the Suggested Markup column that used to sit on the grid
    // deliberately proposed NEGATIVE markups to match a competitor while the
    // quote stayed profitable overall. That column was removed the same day
    // (ClickUp 86d478w8x) and the owner chose to block negative realised markup
    // here. A line that legitimately sells below cost now cannot be confirmed —
    // that is the decision, not an oversight.
    //
    // D119.17.1: on the unit pair, "below" is `costAbovePrice` (the cost at the price's precision),
    // so a line sold AT an exact cost with float noise is not blocked. No unit pair: the markup.
    const unitPair = pricing.unitCost > 0 && pricing.unitSellingPrice > 0;
    const sellsBelowCost = unitPair
      ? costAbovePrice(pricing.unitCost, pricing.unitSellingPrice)
      : Number.isFinite(pricing.realizedMarkup) && pricing.realizedMarkup < 0;
    if (sellsBelowCost) {
      add(
        CONFIRMATION_BLOCKER_CODES.NEGATIVE_MARKUP,
        `${lineLabel(item, index)} sells below cost.`
      );
    }
  });

  return blockers;
}

/** True when nothing blocks this quotation from being confirmed. */
export function canConfirmQuotation(
  quotation: any,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): boolean {
  return confirmationBlockers(quotation, defaultMarkupPercent).length === 0;
}

/** One sentence summarising a blocker list, for a dialog title or an error message. */
export function describeConfirmationBlockers(blockers: ConfirmationBlocker[]): string {
  const list = Array.isArray(blockers) ? blockers : [];
  if (list.length === 0) return '';
  const noun = list.length === 1 ? 'problem' : 'problems';
  return `This quotation cannot be confirmed: ${list.length} ${noun} must be fixed first.`;
}

/**
 * D105.2 (owner, 2026-10-07, the end-to-end retest of QUO-00241): a quotation with any line at
 * US$0.00 cannot be SENT to the client - "the same rule as confirming it" - which is rule 1 of this
 * gate, "every line priced". D105.7 (owner, same day): Send uses the same price rules as Confirm, so
 * rule 2, a negative selling price, stops it too. Selling below cost (rule 3) is still the clerk's
 * call, and what the vessel asked for (rule 4) has its own door in `quotationStatus.service`. So a
 * send asks THIS gate and keeps the entries carrying the codes below - one owner, two callers. A
 * rule that should also stop a send is one more code here.
 */
const SEND_BLOCKER_CODES: readonly string[] = Object.freeze([
  CONFIRMATION_BLOCKER_CODES.NOT_PRICED,
  CONFIRMATION_BLOCKER_CODES.NEGATIVE_SELLING_PRICE,
]);

/** The code the whole refusal carries when a send is refused, whatever the lines are. */
export const QUOTATION_NOT_SENDABLE = 'QUOTATION_NOT_SENDABLE';

/**
 * Everything blocking this quotation from being sent, one entry per line, in line order, in the
 * confirmation gate's own shape: `{ code, lineNumber, itemName, message }`. Empty means it may be
 * sent as far as this rule goes.
 */
export function sendBlockers(
  quotation: any,
  defaultMarkupPercent: number = DEFAULT_MARKUP_PERCENT
): ConfirmationBlocker[] {
  return confirmationBlockers(quotation, defaultMarkupPercent).filter((b) => SEND_BLOCKER_CODES.includes(b.code));
}
